import { forwardRef } from "react";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";

export const isAndroidWebView = () =>
  (window.AndroidBridge && typeof window.AndroidBridge.printImage === "function") ||
  /Android/i.test(navigator.userAgent);

/**
 * Hidden, off-screen receipt DOM node used as the html2canvas source
 * for the Android/native-bridge print path. Not shown to the user.
 * receiptData shape: { date, userName, animalType, weight, fat, snf, price, total }
 */
export const ReceiptPrintTemplate = forwardRef(({ receiptData, t }, ref) => {
  if (!receiptData) return null;

  return (
    <div
      ref={ref}
      className="absolute left-[-9999px] top-0 w-[300px] bg-white p-4"
      style={{ fontFamily: "'Courier New', monospace", color: "#000" }}
    >
      <div className="text-center font-bold text-lg mb-2 pb-1 border-b border-dashed border-gray-800">
        {t("receipt.title")}
      </div>

      <div className="mb-3 space-y-1">
        <div className="flex justify-between"><span>{t("receipt.date")}:</span><span>{receiptData.date}</span></div>
        <div className="flex justify-between"><span>{t("receipt.user")}:</span><span>{receiptData.userName}</span></div>
      </div>

      <div className="border-t border-dashed border-gray-800 my-2"></div>

      <div className="mb-3 space-y-1">
        <div className="flex justify-between"><span>{t("receipt.animal")}:</span><span>{receiptData.animalType || "-"}</span></div>
        <div className="flex justify-between"><span>{t("receipt.weight")}:</span><span>{receiptData.weight} {t("receipt.ltr")}</span></div>
        <div className="flex justify-between"><span>{t("receipt.fat")}:</span><span>{receiptData.fat}%</span></div>
        {receiptData.snf && (
          <div className="flex justify-between"><span>{t("receipt.snf")}:</span><span>{receiptData.snf}%</span></div>
        )}
        <div className="flex justify-between"><span>{t("receipt.rate")}:</span><span>₹{receiptData.price}</span></div>
      </div>

      <div className="border-t border-dashed border-gray-800 my-2"></div>

      <div className="flex justify-between font-bold text-base">
        <span>{t("receipt.total")}:</span><span>₹{receiptData.total}</span>
      </div>

      <div className="text-center text-xs mt-4 text-gray-600">
        {t("receipt.generated")} {new Date().toLocaleString()}
      </div>
    </div>
  );
});

ReceiptPrintTemplate.displayName = "ReceiptPrintTemplate";

/**
 * Prints a receipt either via native Android bridge (image, using the
 * ReceiptPrintTemplate ref) or a browser popup window (raw HTML string).
 *
 * @param {Object} params
 * @param {Object} params.receiptData - normalized receipt fields
 * @param {React.RefObject} params.receiptRef - ref to ReceiptPrintTemplate's DOM node (Android path only)
 * @param {Function} params.t - translation function
 */
export const printReceipt = async ({ receiptData, receiptRef, t }) => {
    console.log("print recoipt is called")
  if (!receiptData) {
    console.log("no Recipt Data");
    return;
  }
  if (isAndroidWebView()) {
    try {
      if (!receiptRef?.current) {
        toast.error(t("receipt.receiptNotFound"));
        return;
      }
      const canvas = await html2canvas(receiptRef.current, { scale: 2, backgroundColor: "#ffffff" });
      const base64Image = canvas.toDataURL("image/png");
      if (window.AndroidBridge && typeof window.AndroidBridge.printImage === "function") {
        window.AndroidBridge.printImage(base64Image);
      } else {
        toast.error(t("receipt.printNotSupported"));
      }
    } catch (error) {
      console.error("Printing failed:", error);
      toast.error(t("receipt.generationFailed"));
    }
    return;
  }

  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    toast.error(t("receipt.popupBlocked"));
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${t("receipt.title")}</title>
        <style>
          body { font-family: 'Courier New', monospace; max-width: 300px; margin: 0 auto; padding: 15px; text-align: center; }
          .header { font-weight: bold; font-size: 18px; border-bottom: 1px dashed #000; margin-bottom: 10px; padding-bottom: 5px; }
          .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .divider { border-top: 1px dashed #000; margin: 10px 0; }
          .total { font-weight: bold; font-size: 16px; }
          .footer { font-size: 12px; margin-top: 20px; color: #555; }
        </style>
      </head>
      <body>
        <div class="header">${t("receipt.title")}</div>
        <div class="row"><span>${t("receipt.date")}:</span><span>${receiptData.date}</span></div>
        <div class="row"><span>${t("receipt.user")}:</span><span>${receiptData.userName}</span></div>
        <div class="divider"></div>
        <div class="row"><span>${t("receipt.animal")}:</span><span>${receiptData.animalType || "-"}</span></div>
        <div class="row"><span>${t("receipt.weight")}:</span><span>${receiptData.weight} ${t("receipt.ltr")}</span></div>
        <div class="row"><span>${t("receipt.fat")}:</span><span>${receiptData.fat}%</span></div>
        ${receiptData.snf ? `<div class="row"><span>${t("receipt.snf")}:</span><span>${receiptData.snf}%</span></div>` : ""}
        <div class="row"><span>${t("receipt.rate")}:</span><span>₹${receiptData.price}</span></div>
        <div class="divider"></div>
        <div class="row total"><span>${t("receipt.total")}:</span><span>₹${receiptData.total}</span></div>
        <div class="footer">${t("receipt.generated")} ${new Date().toLocaleString()}</div>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};
